// What the dashboard's policy cards show, worked out from a raw policy as
// GET /api/policy/v1/policy/:code returns it (not the mapApiFormatToQuote
// shape, which drops `type`). Pure functions, so the card stays presentational.

import moment from 'moment'
import { ADD_ONS, addOnChoice } from '@/helpers/quoteAddOns'
import { coverPrice } from '@/helpers/quote-price'

export const LIVE_STATUSES = [1, 7, 8]

export function isTheftOnly(policy) {
  return policy?.type === 'theft'
}

export function coverLabel(policy) {
  return isTheftOnly(policy) ? 'Theft only' : 'Theft + damage'
}

export function formatDate(date) {
  return date ? moment(date).format('D MMM YYYY') : ''
}

// "£110" for whole pounds, "£4.58" otherwise — the cards never show ".00".
export function formatMoney(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
    maximumFractionDigits: 2,
  })
}

// { monthly, annual } for the cover the policy actually has: price.theft for
// Theft only, price.default for Theft + damage. The top level is only a
// fallback for policies whose price carries no per-type breakdown. Null when
// there's no usable annual figure.
export function policyPrice(policy) {
  const type = isTheftOnly(policy) ? 'theft' : 'default'
  return coverPrice(policy?.price, type) || coverPrice(policy?.price)
}

// The headline price and the line under it: the monthly instalment for
// monthly policies, the annual figure otherwise. Null when there's nothing to
// show, rather than a "£0".
export function priceSummary(policy) {
  const price = policyPrice(policy)
  if (!price) return null
  const renews = formatDate(policy?.period?.expiry_date)

  if (isMonthly(policy)) {
    return {
      amount: formatMoney(price.monthly),
      period: 'a month',
      detail: `12 instalments · ${formatMoney(price.annual)} a year`,
    }
  }
  return {
    amount: formatMoney(price.annual),
    period: 'a year',
    detail: renews ? `Paid in full · renews ${renews}` : 'Paid in full',
  }
}

// --- Claim readiness ----------------------------------------------------
//
// Every stored photo carries a file_type: bicycle | serial_number |
// proof_of_purchase | lock. Photos from before file_type existed were
// backfilled to 'bicycle', so a missing one is read the same way.

export function photosOfType(bike, fileType) {
  return (bike?.photos || []).filter(
    (photo) => (photo.file_type || 'bicycle') === fileType
  )
}

export function hasSerial(bike) {
  return !!bike?.serial_number || photosOfType(bike, 'serial_number').length > 0
}

// `purpose` is what the AssetVerify handoff collects for that item — see
// helpers/assetverify.js. A policy-wide item is done once every bike has it,
// except the lock, which one photo covers.
const READINESS_ITEMS = [
  {
    key: 'lock',
    title: 'Add your lock',
    doneTitle: 'Lock added',
    hint: 'A photo of the lock you use',
    purpose: 'lock',
    perBike: false,
    isDone: (bike) => photosOfType(bike, 'lock').length > 0,
  },
  {
    key: 'serial_number',
    title: 'Add serial number',
    doneTitle: 'Serial number added',
    hint: 'Needed to settle a theft claim',
    purpose: 'serial_number',
    perBike: true,
    isDone: hasSerial,
  },
  {
    key: 'photos',
    title: 'Add bike photos',
    doneTitle: 'Bike photos added',
    hint: 'Proves condition at start of cover',
    purpose: 'photos',
    perBike: true,
    isDone: (bike) => photosOfType(bike, 'bicycle').length > 0,
  },
  {
    key: 'proof_of_purchase',
    title: 'Upload purchase receipt',
    doneTitle: 'Purchase receipt added',
    hint: 'Confirms value',
    purpose: 'proof_of_purchase',
    perBike: true,
    isDone: (bike) => photosOfType(bike, 'proof_of_purchase').length > 0,
  },
]

export function claimReadiness(bikes = []) {
  const items = READINESS_ITEMS.map((item) => {
    const missing = bikes.filter((bike) => !item.isDone(bike))
    const done =
      bikes.length > 0 &&
      (item.perBike ? !missing.length : missing.length < bikes.length)
    return {
      key: item.key,
      title: done ? item.doneTitle : item.title,
      hint: item.hint,
      purpose: item.purpose,
      done,
      // The bike the "Add" link should open AssetVerify on.
      bikeId: done ? null : (missing[0] || bikes[0])?.dbid ?? null,
    }
  })
  const doneCount = items.filter((i) => i.done).length
  return {
    items,
    doneCount,
    total: items.length,
    complete: doneCount === items.length,
  }
}

// --- What's included ----------------------------------------------------

const addOn = (key) => ADD_ONS.find((a) => a.key === key)

// The API returns "not added" as 'none', 0, false, '' or missing, depending on
// the field — the same normalisation policy-edit does before showing it.
function addOnLabel(key, value) {
  if (!value || value === 'none' || value === '0') return null
  const choice = addOnChoice(addOn(key), value)
  return choice && choice.value !== 'none' ? choice.label : null
}

export function includedCover(policy) {
  const ic = policy?.coverage?.insurance_components || {}
  const rf = policy?.coverage?.risk_factors || {}
  const accessories = addOnLabel('accessories_cover', ic.accessories_cover)
  const injury = addOnLabel('personal_injury', ic.personal_injury)
  const travel = addOnLabel('worldwide_cover', rf.worldwide_cover)

  const theft = { title: 'Theft', detail: 'At home and away', included: true }
  const injuryRow = {
    title: 'Personal injury',
    detail: injury || 'Not added',
    included: !!injury,
  }

  if (isTheftOnly(policy)) {
    return [
      theft,
      injuryRow,
      { title: 'Accidental damage', detail: 'Not added', included: false },
      { title: 'Accessories', detail: 'Not added', included: false },
    ]
  }
  return [
    theft,
    { title: 'Accidental damage', detail: 'Including crashes', included: true },
    { title: 'Malicious damage', detail: 'Vandalism', included: true },
    {
      title: 'Accessories',
      detail: accessories ? `Up to ${accessories}` : 'Not added',
      included: !!accessories,
    },
    injuryRow,
    {
      title: 'Worldwide travel',
      detail: travel || 'Not added',
      included: !!travel,
    },
  ]
}

// Any live policy can be changed online, whichever binder it's on. Same
// rule as the quote/allowMTA getter.
export function canChangePolicy(policy) {
  return policy?.status === 1
}

// --- Policy page --------------------------------------------------------

// One bike's claim-ready items for the policy page: a short label, what's on
// file once done, and what to do while it isn't. Same `purpose` and done
// rules as claimReadiness, applied to a single bike.
const BIKE_READINESS = {
  lock: {
    label: 'Lock',
    todo: 'The lock you use',
    onFile: (bike) => {
      const meta = photosOfType(bike, 'lock')[0]?.metadata || {}
      return meta.lock_name || meta.name || 'Photo on file'
    },
  },
  serial_number: {
    label: 'Serial number',
    todo: 'Under the bottom bracket',
    onFile: (bike) => bike.serial_number || 'Photo on file',
  },
  photos: {
    label: 'Bike photos',
    todo: 'Two clear photos of your bike',
    onFile: (bike) => {
      const n = photosOfType(bike, 'bicycle').length
      return `${n} photo${n === 1 ? '' : 's'}`
    },
  },
  proof_of_purchase: {
    label: 'Purchase receipt',
    todo: 'Proves what you paid',
    onFile: (bike) =>
      photosOfType(bike, 'proof_of_purchase')[0]?.original_filename ||
      'On file',
  },
}

export function bikeReadiness(bike) {
  const { items, doneCount, total, complete } = claimReadiness([bike])
  return {
    doneCount,
    total,
    complete,
    items: items.map((item) => {
      const copy = BIKE_READINESS[item.key]
      return {
        key: item.key,
        purpose: item.purpose,
        done: item.done,
        label: copy.label,
        detail: item.done ? copy.onFile(bike) : copy.todo,
      }
    }),
  }
}

// What every policy of each type covers, as CoverSummaryCard lists it in the
// funnel, with a line of detail where one is safe to state in a few words.
const CORE_COVER = {
  theft: ['Theft from your home', 'Theft away from home'],
  default: [
    'Theft from your home',
    'Theft away from home',
    'Accidental damage',
    'Malicious damage',
    'Cycle hire',
  ],
}
const CORE_COVER_DETAIL = {
  'Theft away from home': 'When locked to an immovable object',
  'Accidental damage': 'Crashes, falls and knocks',
  'Malicious damage': 'Vandalism by someone else',
  'Cycle hire': 'A replacement while your claim is settled',
}

export function coreCover(policy) {
  return CORE_COVER[isTheftOnly(policy) ? 'theft' : 'default'].map((title) => ({
    title,
    detail: CORE_COVER_DETAIL[title],
  }))
}

// The policy's add-ons as rows: for tiered ones, how far along the scale the
// chosen level sits; for yes/no ones, just whether it's on. Theft only lists
// only the add-ons it can carry (addOnsFor).
export function addOnRows(policy) {
  const ic = policy?.coverage?.insurance_components || {}
  const rf = policy?.coverage?.risk_factors || {}
  const raw = {
    accessories_cover: ic.accessories_cover,
    personal_injury: ic.personal_injury,
    public_liability: ic.public_liability,
    loss_of_earnings: ic.loss_of_earnings,
    worldwide_cover: rf.worldwide_cover,
    amateur_racing: rf.amateur_racing,
  }
  const hasInjury = !!addOnLabel('personal_injury', ic.personal_injury)

  return ADD_ONS.filter((a) => !isTheftOnly(policy) || a.theft).map((addOn) => {
    const levels = addOn.choices.filter((c) => c.value !== 'none')
    const tiered = levels.length > 1
    let label = null
    if (addOn.key === 'amateur_racing') {
      const r = raw.amateur_racing
      label = [true, 'true', 1, '1', 'yes'].includes(r) ? 'Added' : null
    } else {
      label = addOnLabel(addOn.key, raw[addOn.key])
    }
    // Public liability is part of every Personal Injury plan.
    const included = addOn.key === 'public_liability' && hasInjury
    const level = levels.findIndex((c) => c.label === label)
    return {
      key: addOn.key,
      title: addOn.title,
      detail: addOn.detail,
      badge: included
        ? 'Included'
        : label || (tiered ? 'No cover' : 'Not added'),
      on: included || !!label,
      scale: tiered
        ? {
            steps: levels.length,
            filled: level + 1,
            from: levels[0].label,
            to: levels[levels.length - 1].label,
          }
        : null,
    }
  })
}

const PAYMENT_METHODS = { 1: 'Card', 2: 'Premfina', 12: 'Direct Debit' }

export function paymentMethod(policy) {
  return PAYMENT_METHODS[policy?.payment_type] || ''
}

export function isMonthly(policy) {
  return policy?.payment_interval === 'monthly'
}

// --- Changing a policy -------------------------------------------------

// The most bikes one policy can hold (same limit as BikesList).
export const MAX_BIKES = 5

export function paymentSummary(policy) {
  const plan = isMonthly(policy) ? 'Paid monthly' : 'Paid annually'
  return [paymentMethod(policy), plan].filter(Boolean).join(' · ')
}

// A link into the policy-edit page, opened at one part of it. See
// SECTION_REFS on that page for what each section does.
export function editLink(policy, section, extra = {}) {
  return {
    path: `/dashboard/policy-edit/${policy.code}`,
    query: { section, ...extra },
  }
}

// What the policy page offers to change, in order: the "What would you like
// to change?" card shows the first four, the chooser sheet all of them.
// Payment has no online flow yet, so it goes to the team.
export function changeOptions(policy) {
  const bikes = policy?.coverage?.bicycles || []
  return [
    {
      key: 'addons',
      icon: 'sliders',
      title: 'Add-ons & limits',
      detail: isTheftOnly(policy)
        ? 'Injury, earnings or damage cover'
        : 'Gear, injury, travel, racing',
      to: editLink(policy, 'addons'),
    },
    {
      key: 'bikes',
      icon: 'bike',
      title: 'Bike details',
      detail: 'Make, model, value',
      to: editLink(policy, 'bikes'),
    },
    bikes.length < MAX_BIKES && {
      key: 'add-bike',
      icon: 'plus',
      title: 'Add a bike',
      detail: 'Cover another bike',
      // 50% off applies only to a policy's second bike.
      badge: bikes.length === 1 ? '50% off' : '',
      to: editLink(policy, 'add-bike'),
    },
    {
      key: 'payment',
      icon: 'card',
      title: 'Payment',
      detail: paymentSummary(policy) || 'Card, monthly or annual',
      // No online card or plan change yet.
      to: '/contact-us',
    },
    {
      key: 'details',
      icon: 'person',
      title: 'Personal details',
      detail: 'Name, address, contact',
      to: editLink(policy, 'details'),
      sheetOnly: true,
    },
  ].filter(Boolean)
}
